/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import { debounce, get, isEmpty, isEqual, isNil } from 'lodash';

import PageHeading from 'components/page/page-heading/PageHeading';
import PageSection from 'components/page/page-section/PageSection';

import CampaignsMessages from '../CampaignsMessages';
import CampaignsTableInfluencerContainer from '../campaigns-table/CampaignsTableInfluencerContainer';
import CreatorResponseTime from '../creator-response-time/CreatorResponseTime';
import ActionLink from 'components/buttons/action-link/ActionLink';

import { __t, applyTranslations } from 'utils/language';
import { isRevoked } from 'utils/subcampaign';
import { TYPE_ACCEPTED, TYPE_PENDING } from 'utils/influencer-subcampaigns';

import { fetchOrganizationSettings } from '../../reducers/organizationSettings';
import { cleanSubcampaigns } from 'modules/campaigns/reducers/campaignSubcampaigns';
import {
  fetchInfluencerSubcampaigns,
  fetchInfluencerSubcampaignsCounts,
  resetInfluencerSubcampaigns
} from 'modules/subcampaigns/reducers/influencerSubcampaigns';
import { fetchOverdueDeliverables, resetOverdueDeliverables } from 'modules/subcampaigns/reducers/overdueDeliverables';
import { fetchInfluencerSelfResponseTime } from 'modules/influencers/reducers/influencerResponseTime';

import './campaign-influencer-dashboard.scss';

applyTranslations(require('./campaign-influencer-dashboard.i18n.json'));

import type {
  DeliverablesOverdueBySubcampaignIdLocal,
  Organization,
  OrganizationSettings,
  Pagination,
  ResponseTime,
  Subcampaign
} from 'types';

type OwnProps = {
  organization: Organization
};

type StateProps = {
  currentOrganizationId: number,
  hasInfluencerSubcampaigns: boolean,
  influencerSubcampaignsCounts: {
    [x: string]: number
  },
  influencerSubcampaignsIsLoading: boolean,
  influencerSubcampaignsList: Array<Subcampaign>,
  influencerSubcampaignsPagination: Pagination,
  organizationSettings: OrganizationSettings,
  isLoading: boolean,
  influencerResponseTime: {
    [x: number]: ResponseTime
  },
  overdueDeliverables: DeliverablesOverdueBySubcampaignIdLocal,
  isCcmEnabled?: boolean
};

type DispatchProps = {
  dispatchCleanSubcampaigns: () => void,
  dispatchFetchOrganizationSettings: (a: number) => void,
  dispatchFetchResponseTime: (a: number) => void,
  dispatchFetchOverdueDeliverables: (overdueDeliverableIds: Array<number>) => void,
  dispatchResetOverdueDeliverables: () => void,
  dispatchFetchInfluencerSubcampaignsCounts: (a: number) => Promise<{
    [x: string]: number
  }>,
  dispatchFetchInfluencerSubcampaigns: (c: number, b: string, a: number) => void,
  dispatchResetInfluencerSubcampaigns: () => void
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  page: number,
  activeCampaignState: string
};

class CampaignsInfluencerDashboard extends React.Component<Props, State> {
  containerRef = React.createRef<HTMLDivElement>();

  isMountedRef = false;

  debounceFetchCampaigns: (a: Props) => void;

  state = {
    page: 1,
    activeCampaignState: TYPE_ACCEPTED
  };

  constructor(props: Props) {
    super(props);

    this.debounceFetchCampaigns = debounce(this.fetchCampaigns, 500);
  }

  componentDidMount() {
    const {
      currentOrganizationId,
      dispatchCleanSubcampaigns,
      dispatchFetchOrganizationSettings,
      dispatchFetchInfluencerSubcampaignsCounts
    } = this.props;
    const influencerId = this.getInfluencer().id;
    const influencer = this.getInfluencer();
    this.isMountedRef = true;

    dispatchCleanSubcampaigns();
    dispatchFetchOrganizationSettings(currentOrganizationId);

    if (influencerId) {
      dispatchFetchInfluencerSubcampaignsCounts(influencerId)
        .then((counts) => {
          if (this.isMountedRef && counts[TYPE_PENDING] > 0 && influencer.approvalStatus === 'approved') {
            this.handleActiveCampaignStateChange(TYPE_PENDING);
          }
        });
    }

    this.debounceFetchCampaigns(this.props);
    this.fetchResponseTime();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      organization,
      influencerSubcampaignsList
    } = prevProps;
    const nextOrganization = this.props.organization;

    if (organization && nextOrganization && organization.id !== nextOrganization.id || !isEqual(this.state, prevState)) {
      this.debounceFetchCampaigns(this.props);
    }

    if (influencerSubcampaignsList !== this.props.influencerSubcampaignsList) {
      this.fetchOverdueDeliverables(this.props);
    }
  }

  componentWillUnmount() {
    const {
      dispatchResetInfluencerSubcampaigns,
      dispatchResetOverdueDeliverables
    } = this.props;
    dispatchResetInfluencerSubcampaigns();
    dispatchResetOverdueDeliverables();

    this.isMountedRef = false;
  }

  getInfluencer() {
    const { organization } = this.props;

    const organizationInfluencerProfiles = organization.influencerProfiles || [];
    return organizationInfluencerProfiles[0] || {};
  }

  getProfileBoostStatus = (): boolean => {
    const influencer = this.getInfluencer();
    const hasEthnicGroup = !isNil(influencer.details?.ethnicGroup) && influencer.details?.ethnicGroup !== 'none';
    const hasFashionTags = !isEmpty(influencer.fashionStyles)
      || !isEmpty(influencer.fashionCategories)
      || !isEmpty(influencer.fashionSizes);

    return hasEthnicGroup || hasFashionTags;
  };

  fetchResponseTime() {
    const { dispatchFetchResponseTime } = this.props;

    const influencerId = this.getInfluencer().id;
    dispatchFetchResponseTime(influencerId);
  }

  fetchOverdueDeliverables(nextProps: Props) {
    const {
      dispatchFetchOverdueDeliverables,
      influencerSubcampaignsList
    } = nextProps;

    dispatchFetchOverdueDeliverables((influencerSubcampaignsList || []).filter(s => !isRevoked(s)).map(subcampaign => subcampaign.id));
  }

  fetchCampaigns(nextProps: Props): void {
    const {
      organization,
      dispatchFetchInfluencerSubcampaigns,
      dispatchResetInfluencerSubcampaigns
    } = nextProps;
    const { activeCampaignState, page } = this.state;

    const influencerProfile = get(organization, 'influencerProfiles.0', null);

    if (influencerProfile) {
      dispatchFetchInfluencerSubcampaigns(influencerProfile.id, activeCampaignState, page);
    } else {
      dispatchResetInfluencerSubcampaigns();
    }
  }

  handleActiveCampaignStateChange = (activeCampaignState?: string | null): void => {
    if (activeCampaignState) {
      this.setState({
        activeCampaignState,
        page: 1
      });
    }
  };

  handlePageChange = (page: number): void => {
    this.setState({ page });

    if (this.containerRef.current) {
      this.containerRef.current.scrollIntoView();
    }
  };

  render(): React.ReactNode {
    const {
      organization,
      isLoading,
      hasInfluencerSubcampaigns,
      influencerSubcampaignsIsLoading,
      influencerSubcampaignsList,
      influencerSubcampaignsPagination,
      influencerSubcampaignsCounts,
      overdueDeliverables,
      organizationSettings,
      influencerResponseTime,
      isCcmEnabled
    } = this.props;
    const { activeCampaignState } = this.state;

    if (isLoading) {
      return null;
    }

    const responseTime = influencerResponseTime[this.getInfluencer().id];
    const influencer = this.getInfluencer();

    return (
      <div ref={this.containerRef}>
        <CampaignsMessages
          hasCampaigns={hasInfluencerSubcampaigns || influencerSubcampaignsIsLoading}
          organization={organization}
          organizationSettings={organizationSettings}
        />
        <PageSection
          padding="none"
          style="none"
        >
          <PageHeading
            heading={__t('campaign_influencer_dashboard.my_campaigns_label')}
          />
          {
            hasInfluencerSubcampaigns &&
              <CreatorResponseTime
                responseTime={responseTime}
                creatorProfile={influencer}
              />
          }
          <CampaignsTableInfluencerContainer
            influencerId={influencer.id}
            influencerStatus={influencer.approvalStatus}
            influencerSubcampaignsIsLoading={influencerSubcampaignsIsLoading}
            influencerSubcampaignsList={influencerSubcampaignsList}
            influencerSubcampaignsPagination={influencerSubcampaignsPagination}
            influencerSubcampaignsCounts={influencerSubcampaignsCounts}
            hasProfileBoost={this.getProfileBoostStatus()}
            overdueDeliverables={overdueDeliverables}
            organizationId={organization.id}
            activeCampaignState={activeCampaignState}
            onPageChange={this.handlePageChange}
            onActiveCampaignStateChange={this.handleActiveCampaignStateChange}
          />
        </PageSection>
        {
          isCcmEnabled && (
            <PageSection padding="none">
              <div className="campaign-influencer-dashboard__ccm-on-app">
                <div className="campaign-influencer-dashboard__title">
                  {__t('campaign_influencer_dashboard.ccm_campaign_on_app')}
                </div>
                <div className="campaign-influencer-dashboard__app-link-container">
                  <ActionLink
                    decoration="none"
                    link="https://apps.apple.com/de/app/collabary-influencer-app/id1252570939"
                  >
                    <img
                      src="/static/images/ios-collabary-app.png"
                      className="campaign-influencer-dashboard__app-link"
                      alt="iOS Collabary app"
                    />
                  </ActionLink>
                  <ActionLink
                    decoration="none"
                    link="https://play.google.com/store/apps/details?id=com.collabary.influencer&hl=de"
                  >
                    <img
                      src="/static/images/android-collabary-app.png"
                      className="campaign-influencer-dashboard__app-link"
                      alt="Android Collabary app"
                    />
                  </ActionLink>
                </div>
              </div>
            </PageSection>
          )
        }
      </div>
    );
  }
}

const mapStateToProps = ({
  influencerSubcampaigns,
  organizations,
  organizationSettings,
  overdueDeliverables,
  influencerResponseTime,
  runtimeConfig
}): StateProps => ({
  currentOrganizationId: organizations.currentOrganizationId,
  isLoading: organizationSettings.isLoading,
  influencerSubcampaignsIsLoading: influencerSubcampaigns.isLoading,
  influencerSubcampaignsList: influencerSubcampaigns.list,
  influencerSubcampaignsPagination: influencerSubcampaigns.pagination,
  influencerSubcampaignsCounts: influencerSubcampaigns.counts,
  hasInfluencerSubcampaigns: influencerSubcampaigns.hasCampaigns,
  overdueDeliverables,
  organizationSettings: organizationSettings.organization,
  influencerResponseTime: influencerResponseTime.responseTimeData,
  isCcmEnabled: runtimeConfig.config.isCcmEnabled ?? false
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchCleanSubcampaigns: () => dispatch(cleanSubcampaigns(null)),
  dispatchFetchOrganizationSettings: orgId => dispatch(fetchOrganizationSettings(orgId)),
  dispatchFetchResponseTime: (userId) => dispatch(fetchInfluencerSelfResponseTime(userId)),
  dispatchFetchOverdueDeliverables: subcampaignIds => dispatch(fetchOverdueDeliverables(subcampaignIds)),
  dispatchResetOverdueDeliverables: () => dispatch(resetOverdueDeliverables()),

  dispatchFetchInfluencerSubcampaignsCounts: (influencerProfileId) =>
    dispatch(fetchInfluencerSubcampaignsCounts(influencerProfileId)),

  dispatchFetchInfluencerSubcampaigns: (influencerProfileId, activeCampaignState, page) =>
    dispatch(fetchInfluencerSubcampaigns(influencerProfileId, activeCampaignState, page)),

  dispatchResetInfluencerSubcampaigns: () => dispatch(resetInfluencerSubcampaigns())
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignsInfluencerDashboard);
